/**
 * Direction C, the Patterns tab: the year spiral. Time wraps once a year, January at the
 * top, so the same week of every year lies on one ray. One bead per week: its size is hours
 * worked (WFM days where they exist, else each check's hours spread over its period), its
 * colour the work state. Numbered markers key into the list beside it.
 */
import { dayMs, fmtDate, linear, weeks } from './lifelineModel'

const SIZE = 720
const C = SIZE / 2
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const STATE_COLORS = ['#1f4e8c', '#86abda', '#94692a', '#7b4f94', '#0b7a6c', '#b87503']

export default function YearSpiral({ data, stations, markers }) {
  const ws = weeks(data)
  if (!ws.length) {
    return <div className="ll-card"><h2>Patterns</h2><p className="ll-na"><b>Not available.</b> There is no dated history to wrap into years yet.</p></div>
  }
  const t0 = dayMs(ws[0].start)
  const y0 = Date.UTC(new Date(t0).getUTCFullYear(), 0, 1)
  const yearsSpan = (dayMs(data.as_of) - y0) / (365.25 * 86400000)
  const ring = linear(0, Math.max(1, yearsSpan), 70, C - 60)       // radius: one scale, years outward
  const pos = (ms, extra = 0) => {
    const yrs = (ms - y0) / (365.25 * 86400000)
    const th = 2 * Math.PI * (yrs - Math.floor(yrs))
    const r = ring(yrs) + extra
    return { x: C + r * Math.sin(th), y: C - r * Math.cos(th) }
  }
  const hasHours = ws.some(w => w.hours != null)
  const maxH = Math.max(40, ...ws.map(w => w.hours || 0))
  const rad = linear(0, maxH, 1.5, 6.5)                              // bead size: one scale, hours
  const states = [...new Set(ws.map(w => w.state).filter(Boolean))]
  const colorOf = (st) => STATE_COLORS[Math.max(0, states.indexOf(st)) % STATE_COLORS.length]
  const keyed = [
    ...stations.map(s => ({ day: s.date, kind: 'event', text: `${s.title}${s.changes?.length ? ` · ${s.changes.map(c => c.line).join(', ')}` : ''}` })),
    ...markers.filter(m => m.day).map(m => ({ day: m.check?.pay_date || m.day, kind: m.severity, text: m.finding.title || 'Finding' })),
  ].sort((a, b) => dayMs(a.day) - dayMs(b.day)).map((k, i) => ({ ...k, n: i + 1 }))

  return (
    <div className="ll-row">
      <div className="ll-card ll-card--wide">
        <h2>Year spiral</h2>
        <svg className="ll-spiral" viewBox={`0 0 ${SIZE} ${SIZE}`} role="img"
             aria-label={`Year spiral of ${ws.length} weeks: January at the top, one ring per year, bead size is hours worked`}>
          {MONTHS.map((m, k) => {
            const th = 2 * Math.PI * k / 12
            return (
              <g key={m}>
                <line x1={C} y1={C} x2={C + (C - 40) * Math.sin(th)} y2={C - (C - 40) * Math.cos(th)} stroke="#eceee9" />
                <text x={C + (C - 22) * Math.sin(th)} y={C - (C - 22) * Math.cos(th) + 4} textAnchor="middle" className="ll-mono" style={{ fontSize: 11, fill: 'var(--ll-muted)' }}>{m}</text>
              </g>
            )
          })}
          {ws.map(w => {
            const p = pos(dayMs(w.start) + 3 * 86400000)
            const leave = w.status === 'L' || w.status === 'S'
            const r = hasHours ? rad(w.hours || 0) : 3
            return leave
              ? <circle key={w.i} cx={p.x} cy={p.y} r={3.5} fill="none" stroke="#8a929c" strokeWidth={1.5} />
              : <circle key={w.i} cx={p.x} cy={p.y} r={r} fill={colorOf(w.state)} />
          })}
          {keyed.map(k => {
            const p = pos(dayMs(k.day), 18)
            return (
              <g key={k.n}>
                <circle cx={p.x} cy={p.y} r={9} fill="#fff" stroke={k.kind === 'event' ? 'var(--ll-ink)' : k.kind === 'high' ? 'var(--ll-high)' : 'var(--ll-medium)'} strokeWidth={1.5} />
                <text x={p.x} y={p.y + 3.5} textAnchor="middle" className="ll-mono" style={{ fontSize: 9.5, fontWeight: 600 }}>{k.n}</text>
              </g>
            )
          })}
          <circle cx={C} cy={C} r={58} fill="var(--gray-50)" stroke="var(--ll-rule)" />
          <text x={C} y={C - 4} textAnchor="middle" style={{ fontSize: 14, fontWeight: 700 }}>{[data.person.first_name, data.person.last_name].filter(Boolean).join(' ')}</text>
          <text x={C} y={C + 14} textAnchor="middle" className="ll-mono" style={{ fontSize: 10.5, fill: 'var(--ll-muted)' }}>{fmtDate(ws[0].start)} →</text>
        </svg>
      </div>
      <div className="ll-card">
        <div className="ll-eyebrow">One bead = one week</div>
        <div className="ll-legend">
          {states.map(st => <span key={st}><i style={{ background: colorOf(st), borderRadius: '50%' }} />{st}</span>)}
          <span><i style={{ border: '1.5px solid #8a929c', background: 'transparent', borderRadius: '50%' }} />Leave</span>
        </div>
        <p className="ll-note">{hasHours
          ? `Size = hours worked that week: WFM timecards where they exist${data.sections.pay_statements?.available ? ', otherwise the hours on the pay statements spread over each period' : ''}.`
          : 'No hours data, so every bead is the same size.'}</p>
        <div className="ll-eyebrow">Key</div>
        <div className="ll-key">
          {keyed.map(k => (
            <div key={k.n} style={{ display: 'contents' }}>
              <span className="ll-mono" style={{ fontWeight: 600 }}>{k.n}</span>
              <span className="ll-mono ll-note">{fmtDate(k.day)}</span>
              <span style={{ fontWeight: k.kind === 'event' ? 400 : 600 }}>{k.text}</span>
            </div>
          ))}
          {!keyed.length && <span className="ll-note" style={{ gridColumn: '1 / -1' }}>No events or findings to key.</span>}
        </div>
      </div>
    </div>
  )
}
